import React, { useEffect, useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { GenericPopUp } from "../../components/popup/genericPopUp";
import { metaDataBatchPayload } from "../../types/types";
import {
  getDistinctTagSets,
  createCatalogueSequenceMapping,
  getMappedCatalogueSequences,
} from "../../services/catalogueService";
import "./CatalogueSequencing.css";
import { validateWithOtp } from '../../stateManagement/actions/validateOtpPopupActions';
import CustomSelectWithSearch from '../../features/manageCoupons/components/CustomSelectWithSearch';
import { getNewConfiguration } from "../../services/uiConfigService";
import { Loader } from "../../components/loader/Loader";
import { getLob } from "../../services/authenticationService";
import { updateConfigRequestBody } from "../../services/managePoliciesService";
import { META_DATA_BATCH_API } from "../../services/networkService";

const CatalogueSequencing = () => {
  const [productSets, setProductSets] = useState<string[]>([]);
  const [outletSets, setOutletSets] = useState<string[]>([]);
  const [productSetMap, setProductSetMap] = useState<Record<string, string[]>>({});
  const [outletSetMap, setOutletSetMap] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(false);
  const [popup, setPopup] = useState({ open: false, message: "", type: "Success" });
  const [tempMappedSets, setTempMappedSets] = useState<{ outletSet: string; productSet: string | null }[]>([]);
  const [mappedSets, setMappedSets] = useState<{ outletSet: string; productSet: string | null }[]>([]);
  const [labelColors, setLabelColors] = useState<Record<string, { color: string; label: string }>>({});
  const [metadata, setMetadata] = useState<any>([]);
  const [configData, setConfigData] = useState<any>(null);
  const prioritySet = ['Priority-1', 'Priority-2', 'Priority-3'];
  const hasProductSets = productSets.length > 0;
  const hasOutletSets = outletSets.length > 0;

  const fetchData = async () => {
    setLoading(true);
    try {
      const [productSetRes, outletSetRes, mappedRes, configRes] = await Promise.all([
        getDistinctTagSets('product'),
        getDistinctTagSets('outlet'),
        getMappedCatalogueSequences(),
        getNewConfiguration("catalogue_configuration"),
      ]);
      const productMap = productSetRes.data?.features?.[0] || {};
      const outletMap = outletSetRes.data?.features?.[0] || {};
      // setProductSetMap(productMap);
      // setOutletSetMap(outletMap);
      setProductSets(Object.keys(productMap));
      setOutletSets(Object.keys(outletMap));
      setMappedSets((mappedRes.data?.features || []).map((m: any) => ({ outletSet: m.outletSet, productSet: m.productSet })));
      setTempMappedSets((mappedRes.data?.features || []).map((m: any) => ({ outletSet: m.outletSet, productSet: m.productSet })));
      setConfigData(configRes.domainValues);
      const catalogueConfig = configRes.domainValues?.find((item: any) => item.name === "catalogueSequencing")?.value || [];
      const initialLabelColors: Record<string, { color: string; label: string }> = {};
      prioritySet.forEach((ps) => {
        const configItem = catalogueConfig.find((item: any) => item.productSet === ps);
        initialLabelColors[ps] = {
          color: configItem?.color || '#1976d2',
          label: configItem?.label || '',
        };
      });
      setLabelColors(initialLabelColors);
    } catch (err) {
      setPopup({ open: true, message: "Failed to fetch sets", type: "Error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Sync labelColors to metadata array
  useEffect(() => {
    const metaArr = Object.entries(labelColors).map(([productSet, { label, color }]) => ({
      productSet,
      label,
      color,
    }));
    const catalogueSequencingConfig = {
      name: "catalogueSequencing",
      type: "list",
      value: metaArr
    };
    setMetadata(catalogueSequencingConfig);
  }, [labelColors]);

  // Handler for mapping creation
  const handleCreateMapping = async () => {
    setLoading(true);
    try {
      const newMappings = mappedSets.filter(mappedItem =>
        !tempMappedSets.some(tempItem =>
          tempItem.outletSet === mappedItem.outletSet &&
          tempItem.productSet === mappedItem.productSet
        )
      );

      const unmappedOutletSets = tempMappedSets
        .filter(tempItem =>
          !mappedSets.some(mappedItem => mappedItem.outletSet === tempItem.outletSet)
        )
        .map(tempItem => ({
          outletSet: tempItem.outletSet,
          productSet: null
        }));

      const allChanges = [...newMappings, ...unmappedOutletSets];

      if (allChanges.length === 0) {
        setPopup({
          open: true,
          message: "Please select outlet set(s)",
          type: "Alert"
        });
        return;
      }

      const payload = allChanges.map(mapping => ({
        outletSet: mapping.outletSet,
        productSet: mapping.productSet
      }));

      await createCatalogueSequenceMapping(payload);
      await fetchData();
      setPopup({
        open: true,
        message: "Mappings saved successfully",
        type: "Success"
      });
    } catch (err) {
      setPopup({
        open: true,
        message: "Failed to save mappings",
        type: "Error"
      });
    } finally {
      setLoading(false);
    }
  };

  // Handler for label color change
  const handleLabelColorChange = (productSet: string, color: string) => {
    setLabelColors((prev) => ({
      ...prev,
      [productSet]: {
        ...prev[productSet],
        color,
      },
    }));
  };

  // Handler for label text change
  const handleLabelTextChange = (productSet: string, label: string) => {
    setLabelColors((prev) => ({
      ...prev,
      [productSet]: {
        ...prev[productSet],
        label,
      },
    }));
  };

  // Handler to save label/color metadata
  const handleLabelSave = async () => {
    setLoading(true);
    try {

      const catalogueDomainValues = configData || [];

      const existingIndex = catalogueDomainValues.findIndex(
        (item: any) => item.name === "catalogueSequencing"
      );

      let updatedDomainValues;
      if (existingIndex !== -1) {
        updatedDomainValues = [
          ...catalogueDomainValues.slice(0, existingIndex),
          metadata,
          ...catalogueDomainValues.slice(existingIndex + 1)
        ];
      } else {
        updatedDomainValues = [...catalogueDomainValues, metadata];
      }
      const lob = getLob();
      const requestBody: updateConfigRequestBody = {
        domainName: 'clientconfig',
        domainType: 'catalogue_configuration',
        domainValues: updatedDomainValues,
        lob
      };
      const finalMetaDataPayload: metaDataBatchPayload = {
        features: [requestBody],
      };

      validateWithOtp(
        (verifiedUser?: any, verifyResponse?: any) => {
          try {
            if (verifyResponse && (verifyResponse.status === 201 || verifyResponse.status === 200)) {
              setPopup({ open: true, message: 'Labels and colors saved successfully!', type: 'Success' });
            } else {
              setPopup({ open: true, message: 'Something went wrong!', type: 'Error' });
              throw new Error();
            }
          } catch (error) {
            setPopup({ open: true, message: 'Something went wrong!', type: 'Error' });
          } finally {
            setLoading(false);
          }
        },
        ["demo", "prod", "prod-egtm"],
        "admin",
        META_DATA_BATCH_API,
        finalMetaDataPayload,
        "PUT"
      );
    } catch (e: any) {
      setLoading(false);
      setPopup({ open: true, message: 'Failed to save labels and colors', type: 'Error' });
    }
  };

  return (
    <div className="manage-coupon-parent">
      <GenericPopUp
        message={popup.message}
        type={popup.type as any}
        openGenericModal={popup.open}
        setOpenGenericModal={open => setPopup(prev => ({ ...prev, open: Boolean(open) }))}
      />
      <div className="manage-home-screen-header" style={{ padding: '0px', fontSize: '1.3rem' }}>
        <div className="manage-home-screen-label">
          <span>Catalogue Sequencing</span>
        </div>
      </div>
      {loading ? (
        <Loader sx={{ position: "absolute", top: "55%" }} />
      ) :
        (<Box sx={{ marginLeft: '12px' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            <Typography variant="h6" color="#217ca6" fontWeight={400}>
              Order Tracking Configuration
            </Typography>
            <Typography variant="subtitle1" color="#969696" fontWeight={400} sx={{ mb: 1 }}>
              Add the order status you wish to display in the order tracking page
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, border: "1px solid #E5E5E5", borderRadius: "4px" }}>
            <Box sx={{ display: "flex", p: 1 }} className='coupon-box-header-wrapper'>
              <Typography variant="subtitle1" fontWeight={500} sx={{ width: '30%', pl: 3 }}>Product Set</Typography>
              <Typography variant="subtitle1" fontWeight={500} sx={{ width: '70%' }}>Outlet Set</Typography>
            </Box>
            {!(hasProductSets && hasOutletSets) && (
              <Box sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100px",
              }}>
                <Typography variant="subtitle1" color="#969696" fontWeight={400}>No product and outlet sets found</Typography>
              </Box>
            )}
            {(hasProductSets && hasOutletSets) && productSets.map((productSet) => {

              const currentMappedOutlets = mappedSets
                .filter(m => m.productSet === productSet)
                .map(m => m.outletSet);

              const allMappedOutlets = mappedSets
                .filter(m => m.productSet !== productSet)
                .map(m => m.outletSet);

              return (
                <Box key={productSet} sx={{ display: "flex", gap: 2, alignItems: "center", p: 2 }}>
                  <Box sx={{ width: '30%', pl: 3, color: "#969696" }}>
                    <Typography>{productSet}</Typography>
                  </Box>
                  <Box sx={{ width: '70%' }}>
                    <CustomSelectWithSearch
                      options={outletSets.map(os => ({
                        value: os,
                        label: os,
                        disabled: allMappedOutlets.includes(os) && !currentMappedOutlets.includes(os)
                      }))}
                      label="Select Here"
                      handleChange={(selected) => {
                        const newMappedSets = [
                          ...mappedSets.filter(m => m.productSet !== productSet),
                          ...selected.map(outletSet => ({ outletSet, productSet }))
                        ];
                        setMappedSets(newMappedSets);
                      }}
                      selectedOptions={currentMappedOutlets}
                      multiple={true}
                      searchText=""
                      handleSearch={() => { }}
                      showSelectedAsChip={true}
                      showSearchBar={false}
                      showBorderInChip={true}
                      isDropdownView={true}
                    />
                  </Box>
                </Box>
              );
            })}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="contained" color="primary" onClick={handleCreateMapping} disabled={!(hasProductSets && hasOutletSets) || loading}>
              Save
            </Button>
          </Box>
          {/* Label color configuration section */}
          <Box>
            <Typography variant="h6" color="#217ca6" mb={2} fontWeight={400}>
              Set label for top 3 SKUs
            </Typography>
            {prioritySet.map((ps) => (
              <Box key={ps} sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
                <Typography sx={{ minWidth: 120 }}>{ps}</Typography>
                <TextField
                  label="Label"
                  value={labelColors[ps]?.label || ''}
                  onChange={(e) => handleLabelTextChange(ps, e.target.value)}
                  sx={{ minWidth: 200 }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <input
                    type="color"
                    value={labelColors[ps]?.color || '#1976d2'}
                    onChange={(e) => handleLabelColorChange(ps, e.target.value)}
                    style={{
                      width: '60px',
                      height: '36px',
                      padding: 0,
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  />
                </Box>
              </Box>
            ))}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button variant="contained" color="primary" onClick={handleLabelSave}>
                Save
              </Button>
            </Box>
          </Box>
        </Box>
        )
      }
    </div>
  );
};

export default CatalogueSequencing;